<template>
  <div class="tir-pm-text-editor-file">
    <div
      class="tir-pm-text-editor-file__top-line tir-pm-text-editor-file-top-line"
    >
      <span class="tir-pm-text-editor-file-top-line__name-sign">
        {{ props.label }}
      </span>
      <div class="tir-pm-text-editor-file-top-line__top-settings">
        <TirPmButton
          :variant="TirPmButtonVariantEnum.PrimaryState"
          :size="TirPmButtonSizeEnum.Small"
          title="Сохранить изменения"
          :isDisabled="!isChangedValue"
          @click="onSaveChanges"
          v-if="!isHideMainControls"
        >
          <template #leftSlot>
            <CheckIcon></CheckIcon>
          </template>
        </TirPmButton>
        <TirPmButton
          :variant="TirPmButtonVariantEnum.SecondaryState"
          :size="TirPmButtonSizeEnum.Small"
          title="Отмена"
          @click="onCancelChanges"
          v-if="!isHideMainControls"
        >
          <template #leftSlot>
            <XMarkIcon></XMarkIcon>
          </template>
        </TirPmButton>
        <TirPmPopover
          class="tir-pm-text-editor-file-top-line-popover"
          width="20.188rem"
          :isNoTeleport="true"
        >
          <template #default="{ isOpened }">
            <TirPmButton
              :variant="TirPmButtonVariantEnum.LinkState"
              :size="TirPmButtonSizeEnum.Small"
              title="Настройки"
              :isActive="isOpened || isShowToTopToolbar"
            >
              <template #leftSlot>
                <Cog6ToothIcon></Cog6ToothIcon>
              </template>
            </TirPmButton>
          </template>

          <template #content>
            <div
              class="tir-pm-text-editor-file-top-line-popover__content"
              @click.stop
            >
              <TirCheckbox v-model="isShowToTopToolbar">
                <template #mainSign>Панель инструментов</template>
                <template #hintSign
                  >Можно отключить меню в шапке и вызвать все команды с помощью
                  "/"</template
                >
              </TirCheckbox>
            </div>
          </template>
        </TirPmPopover>
      </div>
    </div>

    <div
      class="tir-pm-text-editor-file__wrapper"
      :class="{
        'tir-pm-text-editor-file__wrapper--focus': isFocus,
        'tir-pm-text-editor-file__wrapper--drag': isDragFile,
      }"
      @dragenter="onDragActive"
      @dragover="onDragActive"
      @dragleave="onDragDeactive"
      @drop="onDragDeactive"
      ref="$wrapper"
    >
      <TirPmTextEditorFileToolbar
        class="tir-pm-text-editor-file__line-tool"
        ref="$toolBar"
        @command="execStyleCommand"
        :isBoldActive="isBoldActive"
        :isItalicActive="isItalicActive"
        :isUnderlineActive="isUnderlineActive"
        :isStrikeActive="isStrikeActive"
        :isSelectedActiveColor="isSelectedActiveColor"
        v-if="editorInlineStyleVisible || !isShowToTopToolbar"
        :isFloatingLine="isShowToTopToolbar"
        :previousLinkContent="previousLinkContent"
        :style="{
          top: isShowToTopToolbar ? positionOfTool.top / 16 + 'rem' : 0,
          left: isShowToTopToolbar ? positionOfTool.left / 16 + 'rem' : 0,
        }"
      />
      <div class="tir-pm-text-editor-file-input-area">
        <div
          :class="[
            'tir-pm-text-editor-file-input-area__content',
            { 'is-press-ctrl': isPressCtrl },
          ]"
          :style="{ 'min-height': minHeightRem }"
          contenteditable="true"
          spellcheck="false"
          @drop="onDrop"
          ref="$editor"
          @input="sendInputEvent"
          @mousedown="onMousedownArea"
          @paste="onPaste"
          :placeholder="props.placeholder"
        ></div>
        <Teleport
          v-if="isRenderTeleport"
          v-for="widget in widgets"
          :key="widget.DataId"
          :to="`.tir-pm-text-editor-file-input-area__content [data-widget-id='${widget.DataId}']`"
        >
          <component
            :is="widget.Component"
            v-bind="widget.Props"
            v-on="widget.Listeners"
          />
        </Teleport>
      </div>
      <div
        class="tir-pm-text-editor-file-input-area-loading"
        v-if="isLoadingFile"
      >
        <TirPmSpinner></TirPmSpinner>
      </div>
      <Transition name="tir-pm-text-editor-file__wrapper__animation">
        <div
          v-if="isToTopButtonVisible"
          class="tir-pm-text-editor-file__wrapper__button-top"
          @click="goToTop"
        >
          <ArrowUpIcon />
        </div>
      </Transition>
    </div>
  </div>
</template>

<script lang="ts" setup generic="T extends ITextEditorFile">
import {
  TirPmButton,
  TirPmButtonVariantEnum,
  TirPmButtonSizeEnum,
} from "tir-pm-button";
import { TirPmPopover } from "tir-pm-popover";
import { TirCheckbox } from "tir-pm-checkbox";
import {
  computed,
  nextTick,
  onMounted,
  onUnmounted,
  PropType,
  reactive,
  ref,
} from "vue";
import { watch } from "vue";

import TirPmTextEditorFileToolbar from "./TirPmTextEditorFileToolbar.vue";
import {
  XMarkIcon,
  Cog6ToothIcon,
  CheckIcon,
  ArrowUpIcon,
} from "tir-style-system/icons/outline";
import { TirPmSpinner } from "tir-pm-spinner";
import { ITextEditorFile } from "./interfaces";
import {
  TextEditorFileExtension,
  FileUploadCallbackFunctionType,
} from "./types";
import { allExtensions } from "./constants";
import {
  useEditorWidgets,
  useFileManager,
  usePasteManager,
  useSaveCaretPosition,
} from "./composables";
const $toolBar = ref<HTMLElement>();
/**События компонента */
const emit = defineEmits<{
  /**Событие обновления значения */
  (e: "update:modelValue", value: string): void;
  /**Событие смены фокуса */
  (e: "isFocus", value: boolean): void;
  /**Событие отмены */
  (e: "cancel"): void;
  /**Событие сохранения */
  (e: "save"): void;
  /**Событие клика по кнопке удаления файла*/
  (e: "onClickDeleteFile", fileId: T["FileId"]): void;
  /**Событие клика по кнопке редактирования файла */
  (e: "onClickEditFile", fileId: T["FileId"]): void;
  /**Событие сохранения названия файла */
  (e: "saveFileName", fileId: T["FileId"], fileName: string): void;
}>();

/**Параметры компонента */
const props = defineProps({
  /**Текст поля ввода */
  modelValue: { type: String as PropType<string>, default: "<div><br/></div>" },
  /**Атрибут placeholder поля ввода */
  placeholder: { type: String as PropType<string>, default: "Введите текст" },
  /**Лейбл над основным инпутом редактора */
  label: { default: "", type: String as PropType<string> },
  /* Высота для редактора */
  minHeight: { type: String as PropType<string>, default: "321" },
  /**Коллбек для загрузки файла */
  fileUploadCallbak: {
    type: Function as PropType<FileUploadCallbackFunctionType>,
  },
  /* Показывать ли кнопки сохранения и отмены */
  isHideMainControls: { type: Boolean as PropType<boolean>, default: false },
  /**Группа уведомлений */
  notificationGroup: {
    type: String,
    default: "general",
  },
  /**Все файлы */
  allFiles: { type: Array as PropType<Array<T>>, default: [] },
  /**Допустимые расширения файла */
  allowedFileExtensions: {
    type: Array as PropType<Array<TextEditorFileExtension>>,
    default: (): Array<TextEditorFileExtension> => allExtensions,
  },
  /**Максимальный размер файла */
  maxFileSize: {
    type: Number,
    default: 26214400.01, // 25 МБ
  },
  /**Загрузка при редактировании названия */
  filesLoadingEditName: {
    type: Array as PropType<Array<T["FileId"]>>,
    default: [],
  },
  /**Область прокрутки */
  scrollArea: { type: HTMLElement, default: () => window },
  /**Высота шапки страницы */
  headerHeight: { type: Number, default: 0 },
});

const isRenderTeleport = ref(false);

/**DOM элемент поля ввода */
const $editor = ref<HTMLElement>();
/**Положение редактора по вертикали */
const editorY = ref(0);
/**Видна ли кнопка для перехода к верхушке редактора */
const isToTopButtonVisible = ref(false);
/**Управление позицией каретки */
const { saveCaretPosition, restoreCaretPosition } =
  useSaveCaretPosition($editor);
/**Управление дропом файлов */
const { onDrop, pasteFile, isLoadingFile } = useFileManager({
  $editor,
  notificationGroup: props.notificationGroup,
  fileUploadCallbak: props.fileUploadCallbak,
  maxFileSize: props.maxFileSize,
  allowedFileExtensions: props.allowedFileExtensions,
  updateValue: sendInputEvent,
});
/**Управление вставкой контента в редактор */
const { onPaste } = usePasteManager(pasteFile, sendInputEvent);

/** Показывать ил сверху тулбар или сделать его плавающим */
const isShowToTopToolbar = ref(false);

/** Сохраненное значение */
const savedValue = ref(props.modelValue);
/**Обновить предыдущее значение */
const updateSavedValue = (value: string) => {
  savedValue.value = value;
};
/** Изменился ли текст по сравнению с modelValue */
const isChangedValue = computed(() => props.modelValue !== savedValue.value);

const minHeightRem = computed(() => {
  const num = parseInt(props.minHeight);
  if (isNaN(num)) return `${321 / 16}rem`;
  return `${num / 16}rem`;
});

const positionOfTool = reactive({
  top: 0,
  left: 0,
});

/** Клик по кнопке отменить */
const onCancelChanges = () => {
  emit("cancel");
};

/** Клик по кнопке сохранить */
const onSaveChanges = () => {
  emit("save");
};
/**Кнопка CTRL зажата */
const isPressCtrl = ref(false);
/**Отображение блока с выбором стилей */
const editorInlineStyleVisible = ref(false);
/**Активен жирный стиль */
const isBoldActive = ref(false);
/**Активен курсив */
const isItalicActive = ref(false);
/**Активно подчеркивание */
const isUnderlineActive = ref(false);
/**Активна пересекающая линия */
const isStrikeActive = ref(false);
/**Активный выделенный текст цветом */
const isSelectedActiveColor = ref("");

/**Активно левое выравнивание */
const isLeftAlignActive = ref(false);
/**Активно по ширине выравнивание */
const isJustifyAlignActive = ref(false);
/**Активно правое выравнивание */
const isRightAlignActive = ref(false);

/**Компонент в фокусе */
const isFocus = ref(false);

/**Позиционирование компонента */

/**Есть ли новые стили */
const isHaveNewStyles = ref(false);

/**DOM элемент обертки компонента */
const $wrapper = ref<HTMLDivElement>();

/**DOM элемент поля ввода */
const isDragFile = ref(false);

const onDragActive = () => {
  isDragFile.value = true;
};

const onDragDeactive = () => {
  isDragFile.value = false;
};

/**При обновлении modelValue */
const onUpdateModelValue = () => setEditorContent(props.modelValue);

/**Отслеживание изменений параметра value */
watch(() => props.modelValue, onUpdateModelValue);
/**Отслеживание изменений поля isFocus */
watch(isFocus, onUpdateIsFocus);

/**Считывание сочетаний клавиш */
async function documentOnKeyDown(key: KeyboardEvent) {
  if (["ControlRight", "ControlLeft"].includes(key.code)) {
    isPressCtrl.value = true;
  }

  if (key.keyCode === 27) {
    emit("cancel");
  }

  if (!isFocus.value) return;

  if (key.keyCode === 13) {
    setStyleStates();
    return false;
  }
  if (key.keyCode === 191 && isShowToTopToolbar.value) {
    htmlEditorInlineStyleShow();

    return false;
  }

  if (!key.ctrlKey && !key.metaKey) return;

  switch (key.code) {
    case "KeyK":
      savveRange = document.getSelection().getRangeAt(0);
      $toolBar.value?.openLincPopover();
      key.preventDefault();
      return;
    case "KeyR":
      execStyleCommand({ command: "justifyRight" });
      key.preventDefault();
      break;
    case "KeyJ":
      execStyleCommand({ command: "justifyCenter" });
      key.preventDefault();
      break;
    case "KeyL":
      execStyleCommand({ command: "justifyLeft" });
      key.preventDefault();
      break;
    case "KeyB":
      execStyleCommand({ command: "bold" });
      key.preventDefault();
      break;
    case "KeyU":
      execStyleCommand({ command: "underline" });
      key.preventDefault();

      break;
    case "KeyX":
      if (key.shiftKey) {
        execStyleCommand({ command: "strikethrough" });
        key.preventDefault();
      }
      break;
    case "KeyI":
      execStyleCommand({ command: "italic" });
      key.preventDefault();
      break;
  }
  // savveRange = null;
  // await sendInputEvent();

  // key.stopPropagation();
}

function documentOnKeyUp(event: KeyboardEvent) {
  if (["ControlRight", "ControlLeft"].includes(event.code)) {
    isPressCtrl.value = false;
  }
}

const { widgets, removeWidgetsFromHTML } = useEditorWidgets({
  content: computed(() => props.modelValue),
  files: computed(() => props.allFiles),
  filesLoadingEditName: computed(() => props.filesLoadingEditName),
  emit: emit,
});

/**
 * * Установить контент в редактор
 * @param content Контент
 */
const setEditorContent = async (content: string) => {
  // Контент на замену
  const contentToReplace = clearText(content);
  // Текущий контент в редакторе
  const currentEditorContent = clearText(
    removeWidgetsFromHTML($editor.value?.innerHTML)
  );
  // Если контент один и тот же
  if (contentToReplace === currentEditorContent) return;
  // Отключаем рендер телепортов
  isRenderTeleport.value = false;
  // Сохраняем позицию каретки
  saveCaretPosition();
  // Обновляем состояние
  $editor.value.innerHTML = contentToReplace;
  // Ждем обновления DOM
  await nextTick();
  // Восстанавливаем каретку
  restoreCaretPosition();
  // Включаем рендер телепортов
  isRenderTeleport.value = true;
};

/**Очистка текста от мусора */
const clearText = (text?: string) => {
  if (!text) return "";
  return text.replace(/<!--{cke_protected}.*?-->|​|&ZeroWidthSpace;/g, "");
};

/**Отправка события при обновлении поля isFocus */
function onUpdateIsFocus() {
  if (!isFocus.value) {
    htmlEditorInlineStyleHide();
  }
  emit("isFocus", isFocus.value);
}

/**Показывать ли уголок всплывашки */
const cornerIsVisible = ref<boolean>(true);

/**Получение позиции выделенного текста */
const getSelectionData = (target: any) => {
  var sel, range;
  var res = { x: 0, y: 0, width: 0, height: 0, text: "" };
  if (target.getSelection) {
    sel = target.getSelection();
    if (sel.rangeCount) {
      range = sel.getRangeAt(0).cloneRange();
      if (range.getClientRects) {
        let a = window.getSelection();
        let b = a?.getRangeAt(0);
        const rects = b?.getClientRects();
        let text;
        if (!rects?.length) return res;
        for (let i = 0; i < rects.length; i++) {
          if (rects[i].x > 0) {
            text = rects[i];
            break;
          }
        }
        if (!text) return undefined;
        let editorRect = $editor.value?.getBoundingClientRect(); // input
        let style = document.getElementById("editor-style")
          ? document.getElementById("editor-style")?.getBoundingClientRect()
              .width || 0
          : 0;

        if (!editorRect) return res;

        res.height = text.height;
        res.width = text.width;
        res.x = text.left - editorRect.left + text.width / 2 - 208 / 2;
        res.y = text.top + text.height - editorRect.top + 10;

        cornerIsVisible.value =
          editorRect.left + text.left + text.width / 2 - style / 2 > 0 &&
          res.x + style + editorRect.left < window.outerWidth;
        if (editorRect.left + text.left + text.width / 2 - style / 2 < 0)
          res.x = -editorRect.left;
        if (res.x + style + editorRect.left >= window.outerWidth)
          res.x = window.outerWidth - style - editorRect.left;
        res.text = sel.toString();
        return res;
      }
    }
  }
  return undefined;
};

/**Сокрытие блока с выбором стилей форматирования */
const htmlEditorInlineStyleHide = () =>
  (editorInlineStyleVisible.value = false);
/**Отображение блока с выбором стилей форматирования */
const htmlEditorInlineStyleShow = () => (editorInlineStyleVisible.value = true);

let savveRange: Range;

function windowOnSelectionChange(e: any) {
  if (e.target && e.target?.closest?.(".tir-pm-text-editor-file-line-tool"))
    return;

  const selection = window.getSelection();
  const editorElement = $editor.value;

  if (!selection || !editorElement) {
    htmlEditorInlineStyleHide();
    return;
  }

  // Проверяем, содержится ли узел начала или конца выделения внутри редактора
  const isInEditor =
    editorElement.contains(selection.anchorNode) &&
    editorElement.contains(selection.focusNode);

  if (!isInEditor) {
    htmlEditorInlineStyleHide();
    return;
  }

  var data = getSelectionData(e.target);
  if (!data) return;

  if (!!data && !!data.text) {
    setStyleStates();
  }
  if (data?.y) {
    positionOfTool.top = data.y;
    positionOfTool.left = Math.max(data.x, 0);
  }

  if (data?.text?.trim()) {
    htmlEditorInlineStyleShow();
    savveRange = document.getSelection().getRangeAt(0);
  } else {
    htmlEditorInlineStyleHide();
  }
}

/**Проверка на клик вне компонента */
function documentOnClick(event: MouseEvent) {
  let target = event.target as HTMLElement;
  let component = $wrapper.value as HTMLElement;
  const isClickedOutside = target != component && !component.contains(target);
  isFocus.value = !isClickedOutside;
}

function wrapTextNodesInDiv(htmlString: string): string {
  // Создаем объект DOMParser
  const parser = new DOMParser();
  // Парсим строку HTML в документ
  const doc = parser.parseFromString(htmlString, "text/html");

  const nodesToMove = new Array<Node>();
  doc.body.childNodes.forEach((node) => {
    // Проверяем, является ли нода текстовой или нодой, которую нужно переместить
    const isNodeToMove =
      node.nodeType === Node.TEXT_NODE ||
      (node.nodeName !== "DIV" &&
        !(node as HTMLElement).hasAttribute("data-widget-id"));

    if (isNodeToMove) {
      nodesToMove.push(node);
    } else if (nodesToMove.length) {
      // Создаем новый div и оборачиваем в него текстовые ноды
      const div = doc.createElement("div");
      nodesToMove.forEach((moveNode) => {
        div.appendChild(moveNode);
      });

      // Вставляем новый div перед текущей нодой
      node.before(div);

      // Очищаем массив nodesToMove после вставки
      nodesToMove.length = 0;
    }
  });

  // Если остались ноды для перемещения, оборачиваем их в div в конце документа
  if (nodesToMove.length) {
    const div = doc.createElement("div");
    nodesToMove.forEach((moveNode) => {
      div.appendChild(moveNode);
    });
    doc.body.appendChild(div);
  }

  // Возвращаем измененную строку HTML
  return doc.body.innerHTML;
}

/**Отправка события о вводе */
async function sendInputEvent() {
  const content = clearText(
    wrapTextNodesInDiv(removeWidgetsFromHTML($editor.value?.innerHTML))
  );
  // Отправляем обновлённое значение родительскому компоненту
  emit("update:modelValue", content);

  // Сбрасываем флаг новых стилей
  isHaveNewStyles.value = false;
}
/**Выполнение команды на форматирование текста */
const execStyleCommand = (command: any) => {
  console.log(command);
  isHaveNewStyles.value = true;
  editorInlineStyleVisible.value = true;
  if (command.command == "createLink") {
    document.getSelection().removeAllRanges();
    document.getSelection().addRange(savveRange);
  }
  if (true) {
    // activeRange();
    document.execCommand(command.command, false, command?.additionalData);
  } else {
    document.execCommand(command.command);
  }
  switch (command) {
    case "bold":
      isBoldActive.value = !isBoldActive.value;
      break;
    case "underline":
      //   setTextColor(activeTextColor.value);
      isUnderlineActive.value = !isUnderlineActive.value;
      break;
    case "strikethrough":
      //   setTextColor(activeTextColor.value);
      isStrikeActive.value = !isStrikeActive.value;
      break;
    case "italic":
      isItalicActive.value = !isItalicActive.value;
      break;
    case "justifyCenter":
      isJustifyAlignActive.value = !isJustifyAlignActive.value;
      break;
    case "justifyLeft":
      isLeftAlignActive.value = !isLeftAlignActive.value;
      break;
    case "justifyRight":
      isRightAlignActive.value = !isRightAlignActive.value;
      break;
  }
  savveRange = null;
  sendInputEvent();
};

/**Получение node элемента, типа не TEXT_NODE */
const getNoTextNode = (node: Node) => {
  if (node.nodeType == 3) return node.parentNode;
  else return node;
};

const convertToHex = (rgba) => {
  let rgb = rgba.replace(/\s/g, "").match(/^rgba?\((\d+),(\d+),?([^,\s)]+)?/i),
    hex = rgb
      ? (rgb[1] | (1 << 8)).toString(16).slice(1) +
        (rgb[2] | (1 << 8)).toString(16).slice(1) +
        (rgb[3] | (1 << 8)).toString(16).slice(1)
      : rgba;
  return "#" + String(hex).toUpperCase();
};

const previousLinkContent = ref("");

const setStyleStates = () => {
  try {
    let range = window.getSelection()?.getRangeAt(0);
    if (!range) return;
    let containers = [range.startContainer, range.endContainer];
    let elements: any[] = containers.map((container) =>
      getNoTextNode(container)
    );

    if (
      range.endContainer.parentElement?.getAttribute("href") &&
      range.startContainer.parentElement?.getAttribute("href")
    ) {
      previousLinkContent.value =
        range.endContainer.parentElement.getAttribute("href");
    }

    let styles: any[] = elements.map((element) =>
      window.getComputedStyle(element)
    );

    isItalicActive.value = styles.some((style) => style.fontStyle === "italic");
    isStrikeActive.value = styles.some((style) =>
      style.textDecorationLine.includes("line-through")
    );
    isUnderlineActive.value = styles.some((style) =>
      style.textDecorationLine.includes("underline")
    );
    isBoldActive.value = styles.some(
      (style) => Number(style.fontWeight) >= 600 || style.fontWeight === "bold"
    );

    isSelectedActiveColor.value = convertToHex(
      styles[0]?.backgroundColor || styles[1]?.backgroundColor
    );
  } catch (e) {
    console.error("Ошибка в setStyleStates:", e);
  }
};

const onMousedownArea = (event: MouseEvent) => {
  const target = event.target as HTMLElement;
  if (isPressCtrl.value && target?.nodeName === "A") {
    const href = target.getAttribute("href");
    if (!href) return;
    event.preventDefault();
    window.open(href, "_blank");
  }
};
/**При прокрутке страницы */
function onScrollWindow() {
  editorY.value = $wrapper.value?.getBoundingClientRect().y;
  isToTopButtonVisible.value = editorY.value - props.headerHeight < 0;
}
/**Проскролить страницу к верху компонента */
const goToTop = () => {
  const top =
    props.scrollArea.scrollTop + editorY.value - props.headerHeight - 2;
  props.scrollArea.scrollTo({ top, behavior: "smooth" });
};

defineExpose({
  /**Обновить предыдущее значение */
  updateSavedValue,
  /** Изменился ли текст по сравнению с modelValue */
  isChangedValue,
});

onMounted(() => {
  isRenderTeleport.value = true;
  setEditorContent(props.modelValue);
  window.addEventListener("keydown", documentOnKeyDown);
  window.addEventListener("keyup", documentOnKeyUp);
  document.addEventListener("selectionchange", setStyleStates);
  document.addEventListener("click", documentOnClick);
  document.addEventListener("selectionchange", windowOnSelectionChange);
  props.scrollArea.addEventListener("scroll", onScrollWindow);
});
/**После удаления компонента */
onUnmounted(() => {
  window.removeEventListener("keydown", documentOnKeyDown);
  window.removeEventListener("keyup", documentOnKeyUp);
  document.removeEventListener("selectionchange", setStyleStates);
  document.removeEventListener("click", documentOnClick);
  document.addEventListener("selectionchange", windowOnSelectionChange);
  props.scrollArea.removeEventListener("scroll", onScrollWindow);
});
</script>

<style lang="scss">
@import "./style.scss";
</style>
